import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api.config';
import AIBackgroundEffects from './AIBackgroundEffects';
import NotificationToast from './NotificationToast';
import './UserManager.css';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalClientes: 0,
    totalAdmins: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: 'all',
    isActive: undefined,
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();

      if (filters.role !== 'all') queryParams.append('role', filters.role);
      if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive);
      if (filters.search) queryParams.append('search', filters.search);
      queryParams.append('page', filters.page);
      queryParams.append('limit', filters.limit);

      const response = await axios.get(
        buildApiUrl(`/api/users?${queryParams.toString()}`),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setUsers(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setNotification({ message: 'Error al cargar usuarios', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        buildApiUrl('/api/users/stats/overview'),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        buildApiUrl(`/api/users/${userId}`),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSelectedUser(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setNotification({ message: 'Error al cargar detalles del usuario', type: 'error' });
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    setConfirmDialog({
      message: `¿Está seguro de ${currentStatus ? 'desactivar' : 'activar'} este usuario?`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('adminToken');
          const response = await axios.put(
            buildApiUrl(`/api/users/${userId}/toggle-active`),
            {},
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (response.data.success) {
            setNotification({ message: response.data.message, type: 'success' });
            fetchUsers();
          }
        } catch (error) {
          console.error('Error toggling user status:', error);
          setNotification({
            message: error.response?.data?.message || 'Error al cambiar estado del usuario',
            type: 'error'
          });
        }
      }
    });
  };

  const resetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      setNotification({ message: 'La contraseña debe tener al menos 6 caracteres', type: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        buildApiUrl(`/api/users/${userId}/reset-password`),
        { newPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setNotification({ message: 'Contraseña reseteada exitosamente', type: 'success' });
        setShowResetPassword(false);
        setNewPassword('');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setNotification({
        message: error.response?.data?.message || 'Error al resetear contraseña',
        type: 'error'
      });
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', className: 'user-manager-badge-super-admin' },
      admin: { label: 'Admin', className: 'user-manager-badge-admin' },
      cliente: { label: 'Cliente', className: 'user-manager-badge-cliente' }
    };
    return badges[role] || badges.cliente;
  };

  const getStatusBadge = (isActive) => {
    return isActive
      ? { label: 'Activo', className: 'user-manager-badge-active' }
      : { label: 'Inactivo', className: 'user-manager-badge-inactive' };
  };

  return (
    <div className="user-manager-container">
      <AIBackgroundEffects step={2} />

      <div className="user-manager-content">
        <div className="user-manager-header">
          <h1>Gestión de Usuarios</h1>
          <p>Administra usuarios, clientes y sus suscripciones</p>
        </div>

        {/* Estadísticas */}
        <div className="user-manager-stats-grid">
          <div className="user-manager-stat-card">
            <div className="user-manager-stat-icon">👥</div>
            <div className="user-manager-stat-info">
              <span className="user-manager-stat-label">Total Usuarios</span>
              <span className="user-manager-stat-value">{stats.totalUsers}</span>
            </div>
          </div>

          <div className="user-manager-stat-card">
            <div className="user-manager-stat-icon">👤</div>
            <div className="user-manager-stat-info">
              <span className="user-manager-stat-label">Clientes</span>
              <span className="user-manager-stat-value">{stats.totalClientes}</span>
            </div>
          </div>

          <div className="user-manager-stat-card">
            <div className="user-manager-stat-icon">🔐</div>
            <div className="user-manager-stat-info">
              <span className="user-manager-stat-label">Administradores</span>
              <span className="user-manager-stat-value">{stats.totalAdmins}</span>
            </div>
          </div>

          <div className="user-manager-stat-card">
            <div className="user-manager-stat-icon">✅</div>
            <div className="user-manager-stat-info">
              <span className="user-manager-stat-label">Usuarios Activos</span>
              <span className="user-manager-stat-value">{stats.activeUsers}</span>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="user-manager-filters-container">
          <div className="user-manager-filter-group">
            <label>Buscar:</label>
            <input
              type="text"
              placeholder="Username, email, negocio..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            />
          </div>

          <div className="user-manager-filter-group">
            <label>Rol:</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value, page: 1 })}
            >
              <option value="all">Todos</option>
              <option value="cliente">Clientes</option>
              <option value="admin">Admins</option>
              <option value="super_admin">Super Admins</option>
            </select>
          </div>

          <div className="user-manager-filter-group">
            <label>Estado:</label>
            <select
              value={filters.isActive === undefined ? 'all' : filters.isActive}
              onChange={(e) => {
                const value = e.target.value === 'all' ? undefined : e.target.value === 'true';
                setFilters({ ...filters, isActive: value, page: 1 });
              }}
            >
              <option value="all">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
        </div>

        {/* Tabla de Usuarios */}
        {loading ? (
          <div className="user-manager-loading-container">
            <div className="user-manager-spinner-large"></div>
            <p>Cargando usuarios...</p>
          </div>
        ) : (
          <>
            <div className="user-manager-table-container">
              <table className="user-manager-users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Negocio</th>
                    <th>Estado</th>
                    <th>Registro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleBadge = getRoleBadge(user.role);
                    const statusBadge = getStatusBadge(user.isActive);

                    return (
                      <tr key={user._id}>
                        <td className="user-manager-username-cell">{user.username}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`user-manager-badge ${roleBadge.className}`}>
                            {roleBadge.label}
                          </span>
                        </td>
                        <td>{user.businessName || '-'}</td>
                        <td>
                          <span className={`user-manager-badge ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td className="user-manager-actions-cell">
                          <button
                            className="user-manager-btn-view"
                            onClick={() => fetchUserDetails(user._id)}
                            title="Ver detalles"
                          >
                            👁️
                          </button>
                          <button
                            className={user.isActive ? 'user-manager-btn-deactivate' : 'user-manager-btn-activate'}
                            onClick={() => toggleUserActive(user._id, user.isActive)}
                            title={user.isActive ? 'Desactivar' : 'Activar'}
                          >
                            {user.isActive ? '🔒' : '🔓'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {users.length === 0 && (
                <div className="user-manager-no-data">
                  No se encontraron usuarios con los filtros seleccionados
                </div>
              )}
            </div>

            {/* Paginación */}
            {pagination.totalPages > 1 && (
              <div className="user-manager-pagination">
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  disabled={filters.page === 1}
                  className="user-manager-pagination-btn"
                >
                  ← Anterior
                </button>

                <span className="user-manager-pagination-info">
                  Página {pagination.currentPage} de {pagination.totalPages}
                  ({pagination.totalRecords} usuarios)
                </span>

                <button
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  disabled={filters.page >= pagination.totalPages}
                  className="user-manager-pagination-btn"
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Detalles */}
      {showModal && selectedUser && (
        <div className="user-manager-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="user-manager-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-manager-modal-header">
              <h2>Detalles del Usuario</h2>
              <button className="user-manager-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="user-manager-modal-body">
              {/* Información del Usuario */}
              <div className="user-manager-detail-section">
                <h3>Información General</h3>
                <div className="user-manager-detail-grid">
                  <div className="user-manager-detail-item">
                    <strong>Username:</strong> {selectedUser.user.username}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Email:</strong> {selectedUser.user.email}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Rol:</strong> <span className={`user-manager-badge ${getRoleBadge(selectedUser.user.role).className}`}>
                      {getRoleBadge(selectedUser.user.role).label}
                    </span>
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Estado:</strong> <span className={`user-manager-badge ${getStatusBadge(selectedUser.user.isActive).className}`}>
                      {getStatusBadge(selectedUser.user.isActive).label}
                    </span>
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Nombre Completo:</strong> {selectedUser.user.contactName || '-'}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Teléfono:</strong> {selectedUser.user.phoneNumber || '-'}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Negocio:</strong> {selectedUser.user.businessName || '-'}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Sitio Web:</strong> {selectedUser.user.businessWebsite || '-'}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Registrado:</strong> {new Date(selectedUser.user.createdAt).toLocaleString()}
                  </div>
                  <div className="user-manager-detail-item">
                    <strong>Último Login:</strong> {selectedUser.user.lastLogin ? new Date(selectedUser.user.lastLogin).toLocaleString() : 'Nunca'}
                  </div>
                </div>
              </div>

              {/* Suscripciones del Cliente */}
              {selectedUser.user.role === 'cliente' && (
                <>
                  <div className="user-manager-detail-section">
                    <h3>Suscripciones ({selectedUser.subscriptions?.length || 0})</h3>
                    {selectedUser.subscriptions && selectedUser.subscriptions.length > 0 ? (
                      <div className="user-manager-subscriptions-list">
                        {selectedUser.subscriptions.map((sub) => (
                          <div key={sub._id} className="user-manager-subscription-card">
                            <div className="user-manager-subscription-header">
                              <strong>{sub.businessName}</strong>
                              <span className={`user-manager-badge user-manager-badge-${sub.agentStatus}`}>
                                {sub.agentStatus}
                              </span>
                            </div>
                            <div className="user-manager-subscription-details">
                              <div><strong>Estado:</strong> {sub.subscriptionStatus}</div>
                              <div><strong>Monto:</strong> ${sub.subscriptionAmount}</div>
                              <div><strong>Website:</strong> {sub.businessWebsite}</div>
                              {sub.elevenLabsAgent?.agentId && (
                                <div><strong>Agent ID:</strong> {sub.elevenLabsAgent.agentId}</div>
                              )}
                              <div><strong>Creado:</strong> {new Date(sub.createdAt).toLocaleDateString()}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="user-manager-no-data-small">No tiene suscripciones</p>
                    )}
                  </div>

                  <div className="user-manager-detail-section">
                    <h3>Pagos ({selectedUser.payments?.length || 0})</h3>
                    {selectedUser.payments && selectedUser.payments.length > 0 ? (
                      <div className="user-manager-payments-list">
                        {selectedUser.payments.map((payment) => (
                          <div key={payment._id} className="user-manager-payment-item">
                            <div><strong>${payment.amount}</strong></div>
                            <div>{payment.status}</div>
                            <div>{new Date(payment.createdAt).toLocaleDateString()}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="user-manager-no-data-small">No tiene pagos registrados</p>
                    )}
                  </div>
                </>
              )}

              {/* Acciones de Admin */}
              <div className="user-manager-detail-section">
                <h3>Acciones</h3>
                <div className="user-manager-action-buttons">
                  <button
                    className="user-manager-btn-action user-manager-btn-reset-password"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                  >
                    🔑 Resetear Contraseña
                  </button>
                </div>

                {showResetPassword && (
                  <div className="user-manager-reset-password-form">
                    <input
                      type="password"
                      placeholder="Nueva contraseña (mín. 6 caracteres)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      className="user-manager-btn-action user-manager-btn-confirm"
                      onClick={() => resetPassword(selectedUser.user.id)}
                    >
                      Confirmar Reset
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="user-manager-confirm-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="user-manager-confirm-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-manager-confirm-icon">⚠️</div>
            <h2 className="user-manager-confirm-title">Confirmar Acción</h2>
            <p className="user-manager-confirm-message">{confirmDialog.message}</p>
            <div className="user-manager-confirm-actions">
              <button
                className="user-manager-confirm-btn-cancel"
                onClick={() => setConfirmDialog(null)}
              >
                Cancelar
              </button>
              <button
                className="user-manager-confirm-btn-confirm"
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog(null);
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
